import { aiData } from "../../stores/ai-page.js";

/**
 * AiImage
 * @class
 * @constructor
 * @public
 * @version 1.0.0
 */
class AiImage extends HTMLElement {
  constructor() {
    super();

    this.showError = this.showError.bind(this);
    this.retryCounter = 0;
    this.maxRetries = 4;
    this.retryDelayMs = 5000;
  }

  /**
   * Getters
   */

  /**
   * @type HTMLImageElement | null
   */
  get img() {
    return this.querySelector("img");
  }

  /**
   * @type String | null
   */
  get blockId() {
    return this.getAttribute("data-block-id");
  }

  /**
   * @type String
   */
  get blockImageIndex() {
    return this.getAttribute("data-block-image-index") || '';
  }

  /**
   * @type String | null
   */
  get imageDescription() {
    return this.getAttribute("data-description");
  }

  /**
   * @type boolean
   */
  get showSkeletonLoading() {
    return this.getAttribute("data-skeleton-loading") !== "false";
  }

  /**
   * connectedCallback
   * Render html/css & attatch event listeners
   */
  connectedCallback() {
    this.loadImage();
  }

  /**
   * Loads the image from AI
   */
  async loadImage() {
    const postData = {
      imageDescription: this.imageDescription,
    };
    if (this.showSkeletonLoading) {
      this.classList.add(
        "block",
        "animate-pulse",
        "w-full",
        "h-[300px]",
        "bg-gray-200",
      );
    }

    let json;

    try {
      const response = await fetch(`/api/proxy/img`, {
        body: JSON.stringify(postData),
        headers: {
          "Content-Type": "application/json",
        },
        method: "post",
      });
      if (response.status === 200) {
        json = await response.json();
      } else {
        const text = await response.text();
        this.showError(text);
      }
    } catch (error) {
      this.showError();
    }

    if (json) {
      this.setImage(json.imageUrl);
    }
  }

  /**
   * Sets the image
   */
  setImage(src) {
    if (this.showSkeletonLoading) {
      this.classList.remove(
        "block",
        "animate-pulse",
        "w-full",
        "h-[300px]",
        "bg-gray-200",
      );
    }

    this.img?.setAttribute("src", src);

    const data = aiData.get();
    const id = this.blockId;

    data.pages.forEach((p) => {
      p.content.forEach((block) => {
        if (block.id == id) {
          block[`image${this.blockImageIndex}`] = src;
        }
      });
    });

    aiData.set(data);
  }

  showError(error) {
    console.error("ERROR loading image", error);
    if (this.retryCounter <= this.maxRetries) {
      this.retryCounter += 1;
      setTimeout(() => {
        this.loadImage();
      }, this.retryDelayMs * this.retryCounter);
    } else {
      if (this.showSkeletonLoading) {
        this.classList.remove(
          "block",
          "animate-pulse",
          "w-full",
          "h-[300px]",
          "bg-gray-200",
        );
        this.classList.add("block", "w-full", "h-[300px]", "bg-red-300");
      }
    }
  }
}

if (customElements.get("ai-image") === undefined) {
  window.customElements.define("ai-image", AiImage);
}
